import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, List, ListItemButton, ListItemText, Button, Checkbox, FormControlLabel, Avatar, Select, MenuItem, TextField, FormHelperText, FormControl, InputLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import RefreshIcon from '@mui/icons-material/Refresh';
import { StyledSelectedItem } from '../../conf/constants';
import { FlowStatesTabProps } from '../types/props.types';
import { getStateLabel, getValidStates } from '../utils/flowUtils';

const FlowStatesTab: React.FC<FlowStatesTabProps> = ({
  flowStates,
  selectedState,
  setSelectedState,
  flowStateConfig,
  setFlowStateConfig,
  flowLocations,
  handleDeleteFlowState,
  handleUpdateFlowState,
  setOpenAddFlowStateModal,
  handleUploadStateIcon,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [nextStateError, setNextStateError] = useState<string | null>(null);

  const sortedLocations = useMemo(() => [...flowLocations].sort((a, b) => a.orderIndex - b.orderIndex), [flowLocations]);
  const validStates = useMemo(() => getValidStates(flowStates), [flowStates]);

  useEffect(() => {
    const nextState = flowStateConfig.nextState;
    if (!validStates.includes(nextState)) {
      setNextStateError(`Next state must be either be set to Done or one of the existing states.`);
    } else {
      setNextStateError(null);
    }
  }, [flowStateConfig.nextState, validStates]);

  const handleUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUploadStateIcon(file);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [handleUploadStateIcon]);

  return (
    <>
      <Box
        sx={{
          width: '30%',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Process Flow States
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <List dense>
            {flowStates.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No flow states available
              </Typography>
            ) : (
              flowStates.map((state) => (
                selectedState === state.id ? (
                  <StyledSelectedItem
                    key={state.id}
                    onClick={() => setSelectedState(state.id)}
                    sx={{ p: 2 }}
                  >
                    <ListItemText primary={`[${state.state}] ${state.status}`} />
                  </StyledSelectedItem>
                ) : (
                  <ListItemButton
                    key={state.id}
                    onClick={() => setSelectedState(state.id)}
                    sx={{ padding: 2 }}
                  >
                    <ListItemText primary={`[${state.state}] ${state.status}`} />
                  </ListItemButton>
                )
              ))
            )}
          </List>
        </Box>
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ mr: 1 }}
            onClick={() => setOpenAddFlowStateModal(true)}
          >
            NEW
          </Button>
          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteFlowState}
            disabled={selectedState === null}
          >
            DELETE
          </Button>
        </Box>
      </Box>

      {flowStates.length > 0 && (
        <Box
          sx={{
            width: '70%',
            p: 3,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h6" gutterBottom>Flow State</Typography>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Status</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
              <TextField
                value={flowStateConfig.status}
                label="Status"
                onChange={(e) =>
                  setFlowStateConfig({ ...flowStateConfig, status: e.target.value })
                }
                fullWidth
                size="small"
              />
              <Typography variant="body2">Icon</Typography>
              <Avatar sx={{ bgcolor: 'transparent' }}>
                {flowStateConfig.iconUrl ? (
                  <img
                    src={flowStateConfig.iconUrl}
                    alt="Flow State Icon"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      maxWidth: '20px',
                      maxHeight: '20px',
                    }}
                  />
                ) : null}
              </Avatar>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".jpg,.jpeg,.png,.svg"
                onChange={handleFileChange}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<UploadIcon />}
                onClick={handleUploadClick}
                disabled={selectedState === null}
                sx={{ paddingX: 2 }}
              >
                UPLOAD
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" gutterBottom>State</Typography>
              <TextField
                value={flowStateConfig.state}
                onChange={(e) =>
                  setFlowStateConfig({
                    ...flowStateConfig,
                    state: parseInt(e.target.value) || 0,
                  })
                }
                label="State"
                fullWidth
                size="small"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Next State</Typography>
              <FormControl fullWidth size="small" error={!!nextStateError}>
                <InputLabel id="next-state-label">Next State</InputLabel>
                <Select
                  labelId="next-state-label"
                  value={flowStateConfig.nextState || 0}
                  onChange={(e) =>
                    setFlowStateConfig({
                      ...flowStateConfig,
                      nextState: Number(e.target.value),
                    })
                  }
                  label="Next State"
                >
                  {validStates.map((state) => (
                    <MenuItem key={state} value={state}>
                      {getStateLabel(state, flowStates)}
                    </MenuItem>
                  ))}
                </Select>
                {nextStateError && <FormHelperText>{nextStateError}</FormHelperText>}
              </FormControl>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Reserved state values:
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{ mb: 3 }}
          >
            Posted: 0, Completed: -1, Draft: -2, Cancelled: -3
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Location Tracker</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={flowStateConfig.showLocationTracker}
                    onChange={(e) =>
                      setFlowStateConfig({
                        ...flowStateConfig,
                        showLocationTracker: e.target.checked,
                      })
                    }
                  />
                }
                label="Show Location Tracker"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Location Index</Typography>
              <FormControl fullWidth size="small">
                <InputLabel id="location-index-label">Location Index</InputLabel>
                <Select
                  labelId="location-index-label"
                  value={flowStateConfig.locationIndex}
                  onChange={(e) =>
                    setFlowStateConfig({
                      ...flowStateConfig,
                      locationIndex: Number(e.target.value),
                    })
                  }
                  label="Location Index"
                >
                  <MenuItem value={-1}>[-1] Not used</MenuItem>
                  {sortedLocations.map((location, index) => (
                    <MenuItem key={index} value={location.orderIndex}>
                      [{index}] {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Typography variant="subtitle2" gutterBottom>
            Order Progression and Cancellation
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={flowStateConfig.canClientAdvance}
                  onChange={(e) =>
                    setFlowStateConfig({
                      ...flowStateConfig,
                      canClientAdvance: e.target.checked,
                    })
                  }
                />
              }
              label="Can Client Advance?"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={flowStateConfig.canContractorAdvance}
                  onChange={(e) =>
                    setFlowStateConfig({
                      ...flowStateConfig,
                      canContractorAdvance: e.target.checked,
                    })
                  }
                />
              }
              label="Can Contractor Advance?"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={flowStateConfig.canClientCancel}
                  onChange={(e) =>
                    setFlowStateConfig({
                      ...flowStateConfig,
                      canClientCancel: e.target.checked,
                    })
                  }
                />
              }
              label="Can Client Cancel?"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={flowStateConfig.canContractorCancel}
                  onChange={(e) =>
                    setFlowStateConfig({
                      ...flowStateConfig,
                      canContractorCancel: e.target.checked,
                    })
                  }
                />
              }
              label="Can Contractor Cancel?"
            />
          </Box>
          <Box sx={{ mt: 'auto', pt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleUpdateFlowState}
              disabled={!!nextStateError}
            >
              SAVE
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default FlowStatesTab;